import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    useGetEquipmentsQuery,
    useGetOrganizationQuery,
    useGetTotemQuery,
    useUpdateTotemMutation,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import { ActionsAndSave, EntityPageBreadcrumb, PageActions, PageHeader } from 'components/PageHeader';
import { SectionContainer } from 'components/DetailsView/Section';
import { getTotemFullName } from 'helpers/totemLabel';
import { TotemInfo, type TotemFormData } from 'pages/Organizations/OrganizationDetails/TotemsDetails/TotemInfo';
import { RecentOrders } from 'pages/Orders/components/RecentOrders';
import { EmbeddedSitesTable } from 'pages/Sites/components/EmbeddedSitesTable';
import { OrganizationUsersTable } from 'pages/Users/components/OrganizationUsersTable';

type ParamTypes = {
    totemId: string;
};

export const TotemDetails = () => {
    const { totemId = '' } = useParams<ParamTypes>();

    const {
        data: totemData,
        loading: totemLoading,
        error: totemError,
    } = useGetTotemQuery({
        variables: { totemId },
    });

    const totem = totemData?.totem;
    const { data: organizationData } = useGetOrganizationQuery({
        variables: { organizationId: totem?.organizationId ?? '' },
        skip: !totem?.organizationId,
    });

    const { data: equipmentsData, loading: equipmentsLoading } = useGetEquipmentsQuery();

    const [updateTotem, { loading: updateLoading }] = useUpdateTotemMutation();

    const methods = useForm<TotemFormData>();
    const {
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitSuccessful },
    } = methods;

    // Clear the dirty flag after a successful save (keeps the header save button disabled until the next edit).
    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true });
        }
    }, [isSubmitSuccessful, reset]);

    if (totemLoading || equipmentsLoading) {
        return (
            <Container>
                <CenteredLoader>
                    <Loader />
                </CenteredLoader>
            </Container>
        );
    }

    if (totemError || !totem || !equipmentsData) {
        throw new Error('Une erreur est survenue lors de la récupération du totem');
    }

    const organization = totem.organization;
    const adminOptions = (organizationData?.organization?.admins ?? []).map(({ _id, fullname }) => ({
        value: _id,
        label: fullname,
    }));
    const totemLabel = getTotemFullName(totem);

    const onSubmit = handleSubmit(async (fields) => {
        const { data } = await updateTotem({
            variables: { totemId, fields },
        });
        if (data) {
            const {
                updateTotem: { error, formErrors, updatedTotem },
            } = data;
            if (updatedTotem) {
                toast.success(`Le totem "${totemLabel}" a bien été modifié !`);
            } else if (formErrors) {
                formErrors.forEach(({ sectionName, sectionErrors }, index) => {
                    toast.error(
                        <span key={index}>
                            Erreur dans la section "{sectionName}" :
                            {sectionErrors.map(({ fieldName, fieldError }, errorIndex) => (
                                <span key={errorIndex}>
                                    <br />- {fieldName} : "{fieldError}"
                                </span>
                            ))}
                        </span>,
                        { autoClose: false },
                    );
                });
                if (formErrors.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }
            } else if (error) {
                toast.error(error);
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        }
    });

    return (
        <Container>
            <Helmet title={`${totemLabel} · TOTADMIN`} />
            <PageHeader>
                <EntityPageBreadcrumb organization={organization} totem={totem} />
                <PageActions>
                    <ActionsAndSave
                        actionGroups={[]}
                        onSave={onSubmit}
                        saveDisabled={!isDirty}
                        saveLoading={updateLoading}
                        saveTitle="Enregistrer"
                    />
                </PageActions>
            </PageHeader>
            <Content>
                <FormProvider {...methods}>
                    <Form onSubmit={onSubmit}>
                        <TotemInfo
                            totem={totem}
                            mainUserOptions={adminOptions}
                            equipments={equipmentsData.adminEquipments}
                        />
                    </Form>
                </FormProvider>
                <SectionContainer title="Sites" isInitiallyOpen>
                    <EmbeddedSitesTable sites={totem.sites} />
                </SectionContainer>
                <SectionContainer title="Utilisateurs" isInitiallyOpen>
                    <OrganizationUsersTable organizationId={totem.organizationId} />
                </SectionContainer>
                <SectionContainer title="Commandes récentes" isInitiallyOpen>
                    <RecentOrders
                        orders={totem.recentOrders}
                        totemId={totem._id}
                        organizationId={totem.organizationId}
                    />
                </SectionContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const CenteredLoader = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;
